/**
 * AssemblyTariffCalculator - Landed Cost Calculator for Assemblies
 *
 * This component calculates the total landed cost for an assembly,
 * including Section 232 (Steel/Aluminum) and Section 301 tariffs.
 *
 * Features:
 * - Pro-rata value splitting based on material weights
 * - Auto-suggestion of steel/aluminum values from weight data
 * - Section 301 (China) tariff configuration
 * - Transshipment penalty option
 * - Detailed breakdown of all duties and tariffs
 * - Clipboard report generation for CBP documentation
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { TiCalculator } from "react-icons/ti";
import { IoCubeOutline } from "react-icons/io5";
import { FiCopy, FiCheck, FiAlertTriangle, FiRefreshCw, FiInfo, FiLock, FiUnlock, FiChevronDown, FiChevronUp, FiX } from 'react-icons/fi';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BE_MAIN_URL;

export default function AssemblyTariffCalculator({
    assemblyId,
    weightData,
    materialSummary = {},
    defaultHtsCode = '',
    defaultDutyRate = 0,
    rationale = '',
    folderName = '',
    spacePath = '',
}) {
    const token = sessionStorage.getItem("userToken");

    // Calculate initial values from weight data
    const getInitialValues = useCallback(() => {
        const totalCost = 1000;
        const totalWeight = weightData?.total_weight_kg || 0;

        if (totalWeight > 0) {
            const steelWeight = weightData?.steel_weight_kg || materialSummary?.Steel || 0;
            const aluminumWeight = weightData?.aluminum_weight_kg || materialSummary?.Aluminum || 0;
            const steelPct = steelWeight / totalWeight;
            const aluminumPct = aluminumWeight / totalWeight;

            return {
                totalCost,
                steelValue: Math.round(totalCost * steelPct * 100) / 100,
                aluminumValue: Math.round(totalCost * aluminumPct * 100) / 100,
                steelPct: steelPct * 100,
                aluminumPct: aluminumPct * 100,
            };
        }
        return { totalCost, steelValue: 0, aluminumValue: 0, steelPct: 0, aluminumPct: 0 };
    }, [weightData, materialSummary]);

    const initial = getInitialValues();

    const [formData, setFormData] = useState({
        totalCost: initial.totalCost,
        steelValue: initial.steelValue,
        aluminumValue: initial.aluminumValue,
        coo: 'China',
        htsCode: defaultHtsCode || '',
        generalDutyPercent: defaultDutyRate || 0,
        applyTransshipment: false,
        sec301Applies: true,
        sec301Rate: 25,
        sec301List: 'List 3',
    });

    const [countries, setCountries] = useState([]);
    const [result, setResult] = useState(null);
    const [isCalculating, setIsCalculating] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);

    // Commit functionality state
    const [committedData, setCommittedData] = useState(null);
    const [isThreatenedOpen, setIsThreatenedOpen] = useState(false);
    const [isLoadingCommitted, setIsLoadingCommitted] = useState(false);
    const [isSavingCommitted, setIsSavingCommitted] = useState(false);

    // Separate breakdown into main (non-threatened) and threatened items
    const mainBreakdown = useMemo(() =>
        result?.breakdown?.filter(item => item.type !== 'threatened') || []
    , [result]);

    const threatenedBreakdown = useMemo(() =>
        result?.breakdown?.filter(item => item.type === 'threatened') || []
    , [result]);

    const threatenedTotal = useMemo(() =>
        threatenedBreakdown.reduce((sum, item) => sum + (item.cost || 0), 0)
    , [threatenedBreakdown]);

    // Check if committed values differ from current values
    const hasValuesChanged = useMemo(() => {
        if (!committedData || !committedData.formData) return false;
        const committed = committedData.formData;
        return (
            committed.totalCost !== formData.totalCost ||
            committed.steelValue !== formData.steelValue ||
            committed.aluminumValue !== formData.aluminumValue ||
            committed.coo !== formData.coo ||
            committed.htsCode !== formData.htsCode ||
            committed.generalDutyPercent !== formData.generalDutyPercent
        );
    }, [committedData, formData]);

    // Update form when props change
    useEffect(() => {
        if (defaultHtsCode) {
            setFormData(prev => ({ ...prev, htsCode: defaultHtsCode }));
        }
        if (defaultDutyRate !== undefined) {
            setFormData(prev => ({ ...prev, generalDutyPercent: defaultDutyRate }));
        }
    }, [defaultHtsCode, defaultDutyRate]);

    // Fetch countries on mount
    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const res = await axios.get(`${BASE_URL}/ocr/countries/`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setCountries(res.data.countries || []);
            } catch (err) {
                console.error("Failed to fetch countries", err);
            }
        };
        fetchCountries();
    }, [token]);

    // Load committed calculation from MongoDB on mount
    useEffect(() => {
        if (!assemblyId) return;

        const loadCommittedCalculation = async () => {
            try {
                setIsLoadingCommitted(true);
                const res = await axios.get(
                    `${BASE_URL}/ocr/committed-calculation/${assemblyId}/`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (res.data.success && res.data.committed) {
                    const committed = res.data.committed;
                    setCommittedData({
                        formData: committed.form_data,
                        result: committed.result,
                        timestamp: committed.committed_at
                    });
                    // Also restore the form data and result from committed values
                    if (committed.form_data) {
                        setFormData(committed.form_data);
                    }
                    if (committed.result) {
                        setResult(committed.result);
                    }
                }
            } catch (err) {
                // 404 is expected if no committed calculation exists
                if (err.response?.status !== 404) {
                    console.error("Failed to load committed calculation", err);
                }
            } finally {
                setIsLoadingCommitted(false);
            }
        };

        loadCommittedCalculation();
    }, [assemblyId, token]);

    // Calculate other value
    const otherValue = Math.max(0, formData.totalCost - formData.steelValue - formData.aluminumValue);

    // Handle total cost change - maintain material ratios
    const handleTotalCostChange = (newTotal) => {
        if (formData.totalCost > 0) {
            const steelRatio = formData.steelValue / formData.totalCost;
            const aluminumRatio = formData.aluminumValue / formData.totalCost;
            setFormData({
                ...formData,
                totalCost: newTotal,
                steelValue: Math.round(newTotal * steelRatio * 100) / 100,
                aluminumValue: Math.round(newTotal * aluminumRatio * 100) / 100,
            });
        } else {
            setFormData({ ...formData, totalCost: newTotal });
        }
    };

    // Reset material values to suggested amounts
    const handleResetMaterialValues = () => {
        const values = getInitialValues();
        setFormData(prev => ({
            ...prev,
            steelValue: Math.round(prev.totalCost * (values.steelPct / 100) * 100) / 100,
            aluminumValue: Math.round(prev.totalCost * (values.aluminumPct / 100) * 100) / 100,
        }));
    };

    // Calculate landed cost
    const handleCalculate = async () => {
        try {
            setIsCalculating(true);

            const payload = {
                hts_code: formData.htsCode,
                coo: formData.coo,
                total_cost: formData.totalCost,
                steel_value: formData.steelValue,
                aluminum_value: formData.aluminumValue,
                general_duty_percent: formData.generalDutyPercent,
                sec_301_info: formData.coo === 'China' && formData.sec301Applies
                    ? {
                        applies: true,
                        rate_percent: formData.sec301Rate,
                        list: formData.sec301List,
                    }
                    : undefined,
                apply_transshipment: formData.applyTransshipment,
            };

            const res = await axios.post(
                `${BASE_URL}/ocr/calculate-assembly-landed-cost/`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setResult(res.data);
        } catch (err) {
            console.error("Calculation failed", err);
        } finally {
            setIsCalculating(false);
        }
    };

    // Commit current calculation (persists to MongoDB)
    const handleCommitCalculation = async () => {
        if (!result) return;

        const timestamp = Date.now();
        const commitData = {
            formData: { ...formData },
            result: { ...result },
            timestamp
        };

        // Update local state immediately
        setCommittedData(commitData);

        // Persist to MongoDB if assemblyId is available
        if (assemblyId) {
            try {
                setIsSavingCommitted(true);
                await axios.post(
                    `${BASE_URL}/ocr/committed-calculation/${assemblyId}/`,
                    {
                        formData: formData,  // camelCase to match backend expectation
                        result: result,
                        timestamp: timestamp,
                        type: 'assembly',  // 'type' not 'calculator_type'
                        rationale: rationale,  // Save rationale with committed calculation
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            } catch (err) {
                console.error("Failed to save committed calculation", err);
                // Keep local state even if save fails
            } finally {
                setIsSavingCommitted(false);
            }
        }
    };

    // Clear committed data (removes from MongoDB)
    const handleClearCommit = async () => {
        setCommittedData(null);

        // Delete from MongoDB if assemblyId is available
        if (assemblyId) {
            try {
                await axios.delete(
                    `${BASE_URL}/ocr/committed-calculation/${assemblyId}/`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            } catch (err) {
                console.error("Failed to delete committed calculation", err);
            }
        }
    };

    // Copy report to clipboard (uses committed values if available)
    const handleCopyToClipboard = async () => {
        // Use committed data if available, otherwise current values
        const dataToExport = committedData || { formData, result };
        if (!dataToExport.result) return;

        try {
            setIsGeneratingReport(true);

            const payload = {
                hts_code: dataToExport.formData.htsCode,
                duty_rate: dataToExport.formData.generalDutyPercent,
                coo: dataToExport.formData.coo,
                unit_cost: dataToExport.formData.totalCost,
                quantity: 1,
                steel_value: dataToExport.formData.steelValue,
                aluminum_value: dataToExport.formData.aluminumValue,
                calculation_result: dataToExport.result,
                rationale: rationale,
                material_summary: materialSummary,
                weight_data: weightData,
                is_committed: !!committedData,
                committed_at: committedData?.timestamp || null,
                folder_name: folderName,
                space_path: spacePath,
            };

            const res = await axios.post(
                `${BASE_URL}/ocr/generate-report/`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            await navigator.clipboard.writeText(res.data.report);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 3000);
        } catch (err) {
            console.error("Failed to generate report", err);
        } finally {
            setIsGeneratingReport(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(value);
    };

    return (
        <div className="bg-white rounded-xl shadow-lg border-2 border-[#1e5045] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#1e5045] to-[#225953] px-6 py-4">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <TiCalculator className="w-6 h-6" />
                    Assembly Landed Cost Calculator
                </h3>
                <p className="text-green-100 text-sm mt-1">
                    Calculate duties and tariffs for the complete assembly
                </p>
            </div>

            {/* Loading committed calculation indicator */}
            {isLoadingCommitted && (
                <div className="px-6 py-3 bg-blue-50 border-b border-blue-200">
                    <div className="flex items-center gap-2 text-blue-700">
                        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span className="text-sm font-medium">Loading saved calculation...</span>
                    </div>
                </div>
            )}

            {/* Selected Classification */}
            {formData.htsCode && (
                <div className="px-6 py-3 bg-[#94c43d]/15 border-b border-[#94c43d]/30">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-600">Selected HTS:</span>
                            <span className="font-mono font-bold text-[#1e5045]">{formData.htsCode}</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-600">Duty:</span>
                            <span className="font-semibold text-[#1e5045]">{formData.generalDutyPercent}%</span>
                        </div>
                    </div>
                </div>
            )}

            <div className="p-6 space-y-6">
                {/* Basic Inputs */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Country of Origin */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Country of Origin
                        </label>
                        <select
                            value={formData.coo}
                            onChange={(e) => setFormData({ ...formData, coo: e.target.value })}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e5045] focus:border-transparent"
                        >
                            {countries.map((country) => (
                                <option key={country} value={country}>{country}</option>
                            ))}
                        </select>
                    </div>

                    {/* Total Assembly Cost */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Total Assembly Cost (USD)
                        </label>
                        <input
                            type="number"
                            value={formData.totalCost}
                            onChange={(e) => handleTotalCostChange(parseFloat(e.target.value) || 0)}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e5045] focus:border-transparent"
                            min="0"
                            step="100"
                        />
                    </div>

                    {/* General Duty Rate */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            General Duty Rate (%)
                        </label>
                        <input
                            type="number"
                            value={formData.generalDutyPercent}
                            onChange={(e) => setFormData({ ...formData, generalDutyPercent: parseFloat(e.target.value) || 0 })}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e5045] focus:border-transparent"
                            min="0"
                            step="0.1"
                        />
                    </div>
                </div>

                {/* Section 232 Value Allocation */}
                <div className="border-2 border-dashed border-[#1e5045]/30 rounded-lg p-5 bg-[#f6f4ed]">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                <IoCubeOutline className="w-5 h-5 text-[#1e5045]" />
                                Section 232 Value Allocation
                            </h4>
                            <p className="text-sm text-gray-500 mt-1">
                                Allocate the dollar value of steel and aluminum content
                            </p>
                        </div>
                        <button
                            onClick={handleResetMaterialValues}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#1e5045] hover:bg-green-100 rounded-lg transition-colors"
                        >
                            <FiRefreshCw className="w-4 h-4" />
                            Reset to Suggested
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Steel Value */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Steel Value (USD)
                            </label>
                            <input
                                type="number"
                                value={formData.steelValue}
                                onChange={(e) => setFormData({ ...formData, steelValue: parseFloat(e.target.value) || 0 })}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e5045] focus:border-transparent"
                                min="0"
                                step="10"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Subject to 25% Section 232
                            </p>
                        </div>

                        {/* Aluminum Value */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Aluminum Value (USD)
                            </label>
                            <input
                                type="number"
                                value={formData.aluminumValue}
                                onChange={(e) => setFormData({ ...formData, aluminumValue: parseFloat(e.target.value) || 0 })}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e5045] focus:border-transparent"
                                min="0"
                                step="10"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Subject to 10% Section 232
                            </p>
                        </div>

                        {/* Other Materials (calculated) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Other Materials (USD)
                            </label>
                            <input
                                type="number"
                                value={otherValue}
                                disabled
                                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-100 text-gray-600"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Auto-calculated from total
                            </p>
                        </div>
                    </div>

                    {/* Suggested values info */}
                    {initial.steelPct > 0 || initial.aluminumPct > 0 ? (
                        <div className="mt-4 flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
                            <FiInfo className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-blue-700">
                                <span className="font-medium">Suggested allocation based on weight:</span>{' '}
                                Steel: {initial.steelPct.toFixed(1)}%, Aluminum: {initial.aluminumPct.toFixed(1)}%
                            </p>
                        </div>
                    ) : null}
                </div>

                {/* Section 301 (China) */}
                {formData.coo === 'China' && (
                    <div className="border border-[#f26522]/30 rounded-lg p-4 bg-[#f26522]/10">
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={formData.sec301Applies}
                                onChange={(e) => setFormData({ ...formData, sec301Applies: e.target.checked })}
                                className="w-5 h-5 text-[#f26522] rounded focus:ring-[#f26522]"
                            />
                            <span className="font-medium text-[#f26522]">Apply Section 301 (China) Tariff</span>
                        </label>

                        {formData.sec301Applies && (
                            <div className="mt-4 grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Section 301 List
                                    </label>
                                    <select
                                        value={formData.sec301List}
                                        onChange={(e) => setFormData({ ...formData, sec301List: e.target.value })}
                                        className="w-full px-3 py-2 border border-[#f26522]/30 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#f26522]"
                                    >
                                        <option value="List 1">List 1</option>
                                        <option value="List 2">List 2</option>
                                        <option value="List 3">List 3</option>
                                        <option value="List 4A">List 4A</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Rate (%)
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.sec301Rate}
                                        onChange={(e) => setFormData({ ...formData, sec301Rate: parseFloat(e.target.value) || 0 })}
                                        className="w-full px-3 py-2 border border-[#f26522]/30 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#f26522]"
                                        min="0"
                                        step="0.1"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Transshipment Penalty */}
                <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={formData.applyTransshipment}
                            onChange={(e) => setFormData({ ...formData, applyTransshipment: e.target.checked })}
                            className="w-5 h-5 text-red-600 rounded focus:ring-red-500"
                        />
                        <div className="flex items-center gap-2">
                            <FiAlertTriangle className="w-5 h-5 text-red-600" />
                            <span className="font-medium text-red-900">Apply Transshipment Penalty (40%)</span>
                        </div>
                    </label>
                    <p className="text-sm text-red-700 mt-2 ml-8">
                        Applied in lieu of reciprocal tariffs for suspected transshipment
                    </p>
                </div>

                {/* Calculate Button */}
                <button
                    onClick={handleCalculate}
                    disabled={isCalculating || !formData.htsCode}
                    className={`w-full py-3.5 rounded-lg font-semibold text-lg transition-all flex items-center justify-center gap-2
                        ${isCalculating || !formData.htsCode
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-[#1e5045] to-[#225953] text-white hover:from-[#163a33] hover:to-[#1a4842]'
                        }`}
                >
                    <TiCalculator className="w-6 h-6" />
                    {isCalculating ? 'Calculating...' : 'Calculate Landed Cost'}
                </button>

                {/* Results Section */}
                {result && (
                    <div className="space-y-4">
                        {/* Summary Cards */}
                        <div className="grid grid-cols-3 gap-4">
                            <div className="bg-gradient-to-br from-[#1e5045] to-[#225953] rounded-xl p-5 text-white">
                                <div className="text-sm opacity-80 mb-1">Total Landed Cost</div>
                                <div className="text-2xl font-bold">{formatCurrency(result.landed_cost)}</div>
                            </div>
                            <div className="bg-gradient-to-br from-[#f26522] to-[#d94f0c] rounded-xl p-5 text-white">
                                <div className="text-sm opacity-80 mb-1">Total Duties & Tariffs</div>
                                <div className="text-2xl font-bold">{formatCurrency(result.total_tariff)}</div>
                            </div>
                            <div className="bg-gray-100 rounded-xl p-5">
                                <div className="text-sm text-gray-500 mb-1">Effective Rate</div>
                                <div className="text-2xl font-bold text-gray-900">
                                    {result.effective_rate_percent?.toFixed(2) || '0.00'}%
                                </div>
                            </div>
                        </div>

                        {/* Main Breakdown Table (excludes threatened) */}
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Description</th>
                                        <th className="px-4 py-3 text-center font-semibold text-gray-700">Rate</th>
                                        <th className="px-4 py-3 text-right font-semibold text-gray-700">Amount</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {mainBreakdown.map((item, idx) => (
                                        <tr
                                            key={idx}
                                            className={`${item.type === 'waived' ? 'bg-gray-50 text-gray-400' : ''}
                                                       ${item.is_danger ? 'bg-red-50' : ''}`}
                                        >
                                            <td className="px-4 py-3">
                                                <div className={`font-medium ${item.is_danger ? 'text-red-700' : ''}`}>
                                                    {item.name}
                                                </div>
                                                {item.note && (
                                                    <div className="text-xs text-gray-500 mt-0.5">{item.note}</div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                {item.type === 'waived' ? (
                                                    <span className="text-gray-400 italic">Waived</span>
                                                ) : item.rate}
                                            </td>
                                            <td className="px-4 py-3 text-right font-medium">
                                                {item.type === 'waived' ? '$0.00' : formatCurrency(item.cost)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-[#1e5045] text-white">
                                    <tr>
                                        <td colSpan="2" className="px-4 py-3 font-bold">TOTAL LANDED COST</td>
                                        <td className="px-4 py-3 text-right font-bold text-lg">
                                            {formatCurrency(result.landed_cost)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        {/* Threatened Tariffs - Collapsible FYI Section */}
                        {threatenedBreakdown.length > 0 && (
                            <div className="border-2 border-[#f26522]/30 rounded-lg overflow-hidden bg-[#f26522]/10">
                                {/* Collapsible Header */}
                                <button
                                    onClick={() => setIsThreatenedOpen(!isThreatenedOpen)}
                                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-[#f26522]/20 transition-colors"
                                >
                                    <div className="flex items-center gap-2">
                                        <FiAlertTriangle className="w-5 h-5 text-[#f26522]" />
                                        <span className="font-semibold text-[#f26522]">
                                            Potential Future Tariffs (FYI)
                                        </span>
                                        <span className="px-2 py-0.5 bg-[#f26522]/20 text-[#f26522] text-xs rounded-full font-medium">
                                            {threatenedBreakdown.length} item{threatenedBreakdown.length > 1 ? 's' : ''}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-[#f26522] font-semibold">
                                            +{formatCurrency(threatenedTotal)}
                                        </span>
                                        {isThreatenedOpen ? (
                                            <FiChevronUp className="w-5 h-5 text-[#f26522]" />
                                        ) : (
                                            <FiChevronDown className="w-5 h-5 text-[#f26522]" />
                                        )}
                                    </div>
                                </button>

                                {/* Expanded Content */}
                                {isThreatenedOpen && (
                                    <div className="border-t border-[#f26522]/30">
                                        <div className="px-4 py-2 bg-[#f26522]/15 text-sm text-gray-700">
                                            These tariffs are proposed or threatened but not yet implemented.
                                            Included for planning purposes only.
                                        </div>
                                        <table className="w-full text-sm">
                                            <tbody className="divide-y divide-[#f26522]/20">
                                                {threatenedBreakdown.map((item, idx) => (
                                                    <tr key={idx} className="bg-[#f26522]/5">
                                                        <td className="px-4 py-3">
                                                            <div className="font-medium text-gray-800">
                                                                {item.name}
                                                            </div>
                                                            {item.note && (
                                                                <div className="text-xs text-gray-600 mt-0.5">{item.note}</div>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 text-center text-gray-700">
                                                            {item.rate}
                                                        </td>
                                                        <td className="px-4 py-3 text-right font-medium text-[#f26522]">
                                                            {formatCurrency(item.cost)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot className="bg-[#f26522]/20">
                                                <tr>
                                                    <td colSpan="2" className="px-4 py-2 font-semibold text-[#f26522]">
                                                        POTENTIAL ADDITIONAL
                                                    </td>
                                                    <td className="px-4 py-2 text-right font-bold text-[#f26522]">
                                                        {formatCurrency(threatenedTotal)}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="2" className="px-4 py-2 font-semibold text-[#f26522]">
                                                        POTENTIAL TOTAL (if all enacted)
                                                    </td>
                                                    <td className="px-4 py-2 text-right font-bold text-[#f26522]">
                                                        {formatCurrency(result.landed_cost + threatenedTotal)}
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Commit Calculation Section */}
                        <div className="flex flex-col gap-3">
                            {/* Committed Status Indicator */}
                            {committedData && (
                                <div className={`flex items-center justify-between px-4 py-3 rounded-lg ${
                                    hasValuesChanged
                                        ? 'bg-[#f26522]/10 border-2 border-[#f26522]/40'
                                        : 'bg-[#94c43d]/15 border-2 border-[#94c43d]/40'
                                }`}>
                                    <div className="flex items-center gap-3">
                                        <FiLock className={`w-5 h-5 ${hasValuesChanged ? 'text-[#f26522]' : 'text-[#94c43d]'}`} />
                                        <div>
                                            <span className={`font-semibold ${hasValuesChanged ? 'text-[#f26522]' : 'text-[#1e5045]'}`}>
                                                Calculation Committed
                                            </span>
                                            <span className="text-gray-500 text-sm ml-2">
                                                {new Date(committedData.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                        {hasValuesChanged && (
                                            <span className="px-2 py-1 bg-[#f26522]/20 text-[#f26522] text-xs rounded-full font-medium">
                                                Values changed
                                            </span>
                                        )}
                                    </div>
                                    <button
                                        onClick={handleClearCommit}
                                        className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                                    >
                                        <FiX className="w-4 h-4" />
                                        Clear
                                    </button>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex gap-3">
                                {/* Commit Button */}
                                {!committedData && (
                                    <button
                                        onClick={handleCommitCalculation}
                                        disabled={isSavingCommitted}
                                        className={`flex-1 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2
                                            ${isSavingCommitted
                                                ? 'bg-gray-200 text-gray-500 cursor-wait border-2 border-gray-300'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-gray-300'
                                            }`}
                                    >
                                        <FiUnlock className="w-5 h-5" />
                                        {isSavingCommitted ? 'Saving...' : 'Commit Calculation'}
                                    </button>
                                )}

                                {/* Copy to Clipboard Button */}
                                <button
                                    onClick={handleCopyToClipboard}
                                    disabled={isGeneratingReport}
                                    className={`${committedData ? 'flex-1' : 'flex-1'} py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2
                                        ${copySuccess
                                            ? 'bg-[#94c43d] text-white'
                                            : 'bg-gradient-to-r from-[#1e5045] to-[#225953] text-white hover:from-[#163a33] hover:to-[#1a4842]'
                                        }`}
                                >
                                    {copySuccess ? (
                                        <>
                                            <FiCheck className="w-5 h-5" />
                                            Copied to Clipboard!
                                        </>
                                    ) : (
                                        <>
                                            <FiCopy className="w-5 h-5" />
                                            {isGeneratingReport ? 'Generating...' : (
                                                committedData ? 'Copy Committed Report' : 'Copy Report to Clipboard'
                                            )}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
